<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationStore } from '@/stores/notification.store'

const store = useNotificationStore()
const { items, unread, connected } = storeToRefs(store)

const open = ref(false)

function toggle() {
  open.value = !open.value
  // Opening the panel marks everything read.
  if (open.value) store.markAllRead()
}

function close() {
  open.value = false
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString()
}
</script>

<template>
  <div class="bell" @keydown.esc="close">
    <button
      class="bell-btn"
      type="button"
      :title="connected ? 'Notifications (live)' : 'Notifications (offline)'"
      :aria-label="`Notifications${unread > 0 ? `, ${unread} unread` : ''}`"
      @click="toggle"
    >
      <span class="bell-icon">🔔</span>
      <span v-if="unread > 0" class="badge">{{ unread > 9 ? '9+' : unread }}</span>
    </button>

    <div v-if="open" class="panel card">
      <header class="panel-head">
        <strong>Notifications</strong>
        <span class="conn" :class="{ 'conn--on': connected }">
          {{ connected ? '● Live' : '○ Offline' }}
        </span>
      </header>
      <p v-if="items.length === 0" class="muted empty">No notifications yet.</p>
      <ul v-else class="list">
        <li v-for="n in items" :key="n.id" class="item">
          <span class="message">{{ n.message }}</span>
          <span class="time muted">{{ formatTime(n.occurredAt) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.bell {
  position: relative;
}
.bell-btn {
  position: relative;
  border: none;
  background: none;
  padding: 0.2rem;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
}
.bell-icon {
  filter: grayscale(0.2);
}
.badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 1.1rem;
  height: 1.1rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: var(--danger);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.panel {
  position: absolute;
  right: 0;
  top: 2rem;
  width: 320px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0.75rem;
  z-index: 60;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.conn {
  font-size: 0.78rem;
  color: var(--text-muted);
}
.conn--on {
  color: var(--success);
}
.empty {
  margin: 0;
  font-size: 0.9rem;
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.88rem;
}
.item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.time {
  font-size: 0.75rem;
}
</style>
